/**
 * The sound manifest (ADR-085 decisions 6 and 13): each hook with a sound and its variants, each music folder and its
 * tracks, and each variant's or track's encodings as a media type and a path under `assets/audio/`, which the page
 * takes the first it can play of. The build writes it as `assets/audio/current.json`, served `no-cache`; every file it
 * names is content-hashed and immutable.
 */

/**
 * Where the manifest lives under the asset base.
 */
export const SOUND_MANIFEST = "assets/audio/current.json";

/**
 * The folder every encoded sound lies under.
 */
export const SOUND_ROOT = "assets/audio/";

/**
 * One encoded copy of a variant: its media type and its path from the asset base.
 */
export interface SoundEncoding {
  readonly type: string;
  readonly path: string;
}

/**
 * One variant of a hook: its encodings, the preferred first.
 */
export interface SoundVariant {
  readonly encodings: readonly SoundEncoding[];
}

/**
 * One music track: its title, the purpose its file name's prefix names in its folder (null for none), and its encodings.
 */
export interface MusicTrack extends SoundVariant {
  readonly title: string;
  readonly purpose: string | null;
}

/**
 * The manifest: each hook with at least one variant, and each music folder with at least one track.
 */
export interface SoundManifest {
  readonly sounds: Readonly<Record<string, readonly SoundVariant[]>>;
  readonly music: Readonly<Record<string, readonly MusicTrack[]>>;
}

/**
 * The manifest of a build with no sound.
 */
export const EMPTY_SOUND_MANIFEST: SoundManifest = Object.freeze({ sounds: Object.freeze({}), music: Object.freeze({}) });

/**
 * Whether a path lies under `assets/audio/` and never climbs out of it.
 *
 * @param path The path.
 * @returns True when it does.
 */
function underRoot(path: string): boolean {
  return path.startsWith(SOUND_ROOT) && !path.split("/").includes("..");
}

/**
 * Reads one variant's or track's encodings.
 *
 * @param place The hook and variant, or folder and track, for messages.
 * @param value The parsed variant or track.
 * @returns Its encodings.
 * @throws Error naming the place when an encoding is malformed.
 */
function readEncodings(place: string, value: unknown): SoundEncoding[] {
  const encodings = typeof value === "object" && value !== null ? (value as { encodings?: unknown }).encodings : undefined;
  if (!Array.isArray(encodings) || encodings.length === 0) {
    throw new Error(`${place} must list at least one encoding`);
  }
  return encodings.map((encoding: unknown): SoundEncoding => {
    const record = typeof encoding === "object" && encoding !== null ? (encoding as Readonly<Record<string, unknown>>) : {};
    const type = record["type"];
    const path = record["path"];
    if (typeof type !== "string" || type.length === 0 || typeof path !== "string") {
      throw new Error(`${place}: an encoding needs a type and a path`);
    }
    if (!underRoot(path)) {
      throw new Error(`${place}: a path must lie under ${SOUND_ROOT}`);
    }
    return { type, path };
  });
}

/**
 * Reads one hook's variants.
 *
 * @param hook The hook, for messages.
 * @param value Its parsed variants.
 * @returns The variants.
 * @throws Error naming the hook and variant when one is malformed.
 */
function readVariants(hook: string, value: unknown): SoundVariant[] {
  if (!Array.isArray(value) || value.length === 0) {
    throw new Error(`${hook} must list at least one variant`);
  }
  return value.map((variant: unknown, index): SoundVariant => ({ encodings: readEncodings(`${hook} variant ${index + 1}`, variant) }));
}

/**
 * Reads one music folder's tracks.
 *
 * @param folder The folder, for messages.
 * @param value Its parsed tracks.
 * @returns The tracks.
 * @throws Error naming the folder and track when one is malformed.
 */
function readTracks(folder: string, value: unknown): MusicTrack[] {
  if (!Array.isArray(value) || value.length === 0) {
    throw new Error(`${folder} must list at least one track`);
  }
  return value.map((track: unknown, index): MusicTrack => {
    const place = `${folder} track ${index + 1}`;
    const record = typeof track === "object" && track !== null ? (track as Readonly<Record<string, unknown>>) : {};
    const title = record["title"];
    const purpose = record["purpose"];
    if (typeof title !== "string" || title.length === 0) {
      throw new Error(`${place} needs a title`);
    }
    if (purpose !== null && typeof purpose !== "string") {
      throw new Error(`${place}'s purpose must be text or null`);
    }
    return { title, purpose, encodings: readEncodings(place, track) };
  });
}

/**
 * Reads an object of lists keyed by hook or folder.
 *
 * @param value The parsed object.
 * @param name Its field's name, for messages.
 * @param read Reads one entry's list.
 * @returns The entries.
 * @throws Error when the value is not an object, or an entry is malformed.
 */
function readKeyed<T>(value: unknown, name: string, read: (key: string, entry: unknown) => T): Record<string, T> {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new Error(`the sound manifest must hold a ${name} object`);
  }
  const out: Record<string, T> = {};
  for (const [key, entry] of Object.entries(value as Readonly<Record<string, unknown>>)) {
    out[key] = read(key, entry);
  }
  return out;
}

/**
 * Reads a parsed manifest. A manifest written before music was built has none.
 *
 * @param value The parsed `current.json`.
 * @returns The manifest.
 * @throws Error naming the hook or folder, the variant or track and the field when anything is malformed.
 */
export function parseSoundManifest(value: unknown): SoundManifest {
  const record = typeof value === "object" && value !== null ? (value as { sounds?: unknown; music?: unknown }) : {};
  const sounds = readKeyed(record.sounds, "sounds", readVariants);
  const music = record.music === undefined ? {} : readKeyed(record.music, "music", readTracks);
  return { sounds, music };
}

/**
 * Copies encodings with their fields in a fixed order.
 *
 * @param encodings The encodings.
 * @returns The copies.
 */
function writeEncodings(encodings: readonly SoundEncoding[]): SoundEncoding[] {
  return encodings.map((encoding) => ({ type: encoding.type, path: encoding.path }));
}

/**
 * Writes a manifest as its file's text: hooks and folders in sorted order, two-space indents and a closing newline, so
 * equal manifests are equal files.
 *
 * @param manifest The manifest.
 * @returns The text.
 */
export function serialiseSoundManifest(manifest: SoundManifest): string {
  const sounds: Record<string, readonly SoundVariant[]> = {};
  for (const hook of Object.keys(manifest.sounds).sort()) {
    sounds[hook] = (manifest.sounds[hook] as readonly SoundVariant[]).map((variant) => ({ encodings: writeEncodings(variant.encodings) }));
  }
  const music: Record<string, readonly MusicTrack[]> = {};
  for (const folder of Object.keys(manifest.music).sort()) {
    music[folder] = (manifest.music[folder] as readonly MusicTrack[]).map((track) => ({ title: track.title, purpose: track.purpose, encodings: writeEncodings(track.encodings) }));
  }
  return `${JSON.stringify({ sounds, music }, null, 2)}\n`;
}
